// Professional geometric avatar gallery — fully self-contained: no external
// dependencies and no network calls (so no CSP/privacy concerns). Each avatar
// is a brand-gradient circle with a subtle geometric motif. A chosen avatar is
// stored as an SVG data URL in the user's `profilePhoto`, so it renders
// anywhere a real photo would (team chat, sidebar, profile) with zero extra
// wiring — a real uploaded photo still takes precedence over the avatar.

// Brand-aligned gradient pairs (Deskte dark/cyan palette).
const GRADS = [
  ['#00d4ff', '#005fae'],
  ['#2dd4bf', '#0e7c66'],
  ['#818cf8', '#4f46e5'],
  ['#c084fc', '#7c3aed'],
  ['#62809b', '#26313f'],
  ['#fb7faf', '#be255f'],
  ['#38bdf8', '#0369a1'],
  ['#34d399', '#0f7a52'],
];

const MOTIFS = ['rings', 'hex', 'wave', 'dots', 'orbit', 'bars', 'tri', 'grid'];

// Subtle white geometric overlay for a given motif key.
function motifSvg(m, ov) {
  switch (m) {
    case 'rings':
      return `<circle cx="50" cy="50" r="30" fill="none" stroke="${ov}" stroke-width="3"/><circle cx="50" cy="50" r="17" fill="none" stroke="${ov}" stroke-width="3"/>`;
    case 'hex':
      return `<path d="M50 22 L74 36 L74 64 L50 78 L26 64 L26 36 Z" fill="none" stroke="${ov}" stroke-width="3" stroke-linejoin="round"/>`;
    case 'orbit':
      return `<circle cx="50" cy="50" r="25" fill="none" stroke="${ov}" stroke-width="2.5"/><circle cx="75" cy="50" r="6" fill="#ffffff" fill-opacity="0.92"/>`;
    case 'wave':
      return `<path d="M16 58 Q33 42 50 58 T84 58" fill="none" stroke="${ov}" stroke-width="3.5" stroke-linecap="round"/><path d="M16 44 Q33 28 50 44 T84 44" fill="none" stroke="rgba(255,255,255,0.14)" stroke-width="3.5" stroke-linecap="round"/>`;
    case 'dots': {
      let o = '';
      for (let x = 32; x <= 68; x += 12) for (let y = 32; y <= 68; y += 12) o += `<circle cx="${x}" cy="${y}" r="3.2" fill="${ov}"/>`;
      return o;
    }
    case 'bars':
      return `<rect x="31" y="46" width="8" height="24" rx="3" fill="${ov}"/><rect x="46" y="34" width="8" height="36" rx="3" fill="${ov}"/><rect x="61" y="52" width="8" height="18" rx="3" fill="${ov}"/>`;
    case 'tri':
      return `<path d="M50 30 L72 68 L28 68 Z" fill="none" stroke="${ov}" stroke-width="3" stroke-linejoin="round"/>`;
    default: // 'grid'
      return `<rect x="30" y="30" width="40" height="40" rx="6" fill="none" stroke="${ov}" stroke-width="3"/><line x1="50" y1="30" x2="50" y2="70" stroke="${ov}" stroke-width="2"/><line x1="30" y1="50" x2="70" y2="50" stroke="${ov}" stroke-width="2"/>`;
  }
}

// Build the full SVG markup for one avatar. xmlns is required for the SVG to
// render when used as the `src` of an <img>.
export function buildAvatarSvg(c1, c2, motif) {
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">'
    + `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${c1}"/><stop offset="1" stop-color="${c2}"/></linearGradient></defs>`
    + '<rect width="100" height="100" fill="url(#g)"/>'
    + motifSvg(motif, 'rgba(255,255,255,0.26)')
    + '</svg>';
}

// SVG is pure ASCII (hex colors + numbers), so btoa is safe here.
export function svgToDataUrl(svg) {
  return 'data:image/svg+xml;base64,' + btoa(svg);
}

// Curated gallery: each gradient paired with 3 motifs → 24 distinct avatars,
// so every color appears with varied shapes and shapes spread across colors.
export const AVATAR_PRESETS = (() => {
  const out = [];
  GRADS.forEach(([c1, c2], gi) => {
    for (let k = 0; k < 3; k++) {
      const motif = MOTIFS[(gi + k) % MOTIFS.length];
      out.push({ id: `g${gi}-${motif}`, c1, c2, motif });
    }
  });
  return out;
})();

// ── Professional "person" avatars (faces) ───────────────────────────────
// Clean flat people on a brand-gradient background, varied by skin tone,
// hairstyle, glasses, beard and clothing. Same storage model as the geometric
// avatars: a chosen face is stored as an SVG data URL in profilePhoto.
const SKIN = ['#f3d2b3', '#e7b48d', '#cd9266', '#a66a3c', '#7d4e2e', '#f6dcc4'];
const HAIR = ['#2a2118', '#5a3a22', '#141414', '#9a9a9a', '#c79a4b', '#6b4a2a'];
const CLOTH = ['#1e2b3a', '#243a4a', '#22332b', '#2e2740', '#2a3340', '#3a2630'];

function fHair(style, h) {
  switch (style) {
    case 'short': return `<path d="M30 44 Q28 24 50 24 Q72 24 70 44 Q70 32 58 30 Q54 29 50 29 Q46 29 42 30 Q30 32 30 44 Z" fill="${h}"/>`;
    case 'sidepart': return `<path d="M30 44 Q28 24 50 24 Q72 24 70 44 Q70 31 50 30 Q43 30 39 35 Q34 31 30 44 Z" fill="${h}"/>`;
    case 'bun': return `<path d="M31 42 Q31 26 50 26 Q69 26 69 42 Q69 32 50 31 Q31 32 31 42 Z" fill="${h}"/><circle cx="50" cy="21" r="6.5" fill="${h}"/>`;
    case 'curly': return `<path d="M28 46 Q23 32 31 28 Q33 21 41 24 Q45 20 50 23 Q55 20 59 24 Q67 21 69 28 Q77 32 72 46 Q70 33 50 32 Q30 33 28 46 Z" fill="${h}"/>`;
    case 'buzz': return `<path d="M31 43 Q31 26 50 26 Q69 26 69 43 Q60 33 50 33 Q40 33 31 43 Z" fill="${h}" fill-opacity="0.92"/>`;
    case 'long': return `<path d="M30 42 Q29 25 50 25 Q71 25 70 42 Q70 31 50 30 Q30 31 30 42 Z" fill="${h}"/>`;
    default: return '';
  }
}

// Build one flat-person avatar over a brand gradient.
export function buildFaceSvg(c1, c2, o) {
  let s = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">';
  s += `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${c1}"/><stop offset="1" stop-color="${c2}"/></linearGradient></defs>`;
  s += '<rect width="100" height="100" fill="url(#g)"/>';
  s += `<path d="M20 100 Q20 76 50 76 Q80 76 80 100 Z" fill="${o.cloth}"/>`;
  if (o.style === 'long') s += `<path d="M26 78 Q21 30 50 24 Q79 30 74 78 Q74 50 69 47 Q69 30 50 29 Q31 30 31 47 Q26 50 26 78 Z" fill="${o.hair}"/>`;
  s += `<rect x="44" y="62" width="12" height="12" rx="5" fill="${o.skin}"/>`;
  s += `<circle cx="31" cy="49" r="4" fill="${o.skin}"/><circle cx="69" cy="49" r="4" fill="${o.skin}"/>`;
  s += `<ellipse cx="50" cy="47" rx="19" ry="21" fill="${o.skin}"/>`;
  if (o.beard) s += `<path d="M34 53 Q34 71 50 71 Q66 71 66 53 Q60 66 50 66 Q40 66 34 53 Z" fill="${o.hair}"/>`;
  s += fHair(o.style, o.hair);
  s += '<circle cx="43" cy="48" r="2.1" fill="#33291f"/><circle cx="57" cy="48" r="2.1" fill="#33291f"/>';
  if (o.glasses) s += '<g fill="none" stroke="#33322c" stroke-width="1.5"><rect x="37.5" y="44" width="10" height="7.5" rx="3"/><rect x="52.5" y="44" width="10" height="7.5" rx="3"/><line x1="47.5" y1="47" x2="52.5" y2="47"/></g>';
  s += '<path d="M45.5 56 Q50 59 54.5 56" fill="none" stroke="#b07a63" stroke-width="1.6" stroke-linecap="round"/>';
  s += '</svg>';
  return s;
}

const FACE_STYLES = ['short', 'sidepart', 'bun', 'curly', 'buzz', 'long'];
// 24 faces: gradients cycle × varied skin/hair/style/glasses/beard/clothing for
// broad people variety while staying cohesive with the geometric set.
export const FACE_PRESETS = (() => {
  const out = [];
  for (let i = 0; i < 24; i++) {
    const [c1, c2] = GRADS[i % GRADS.length];
    out.push({
      id: `f${i}`, c1, c2,
      skin: SKIN[i % SKIN.length],
      hair: HAIR[(i * 2) % HAIR.length],
      cloth: CLOTH[i % CLOTH.length],
      style: FACE_STYLES[i % FACE_STYLES.length],
      glasses: i % 3 === 0,
      beard: i % 4 === 1,
    });
  }
  return out;
})();
